import React, { useEffect, useState } from "react";
import { Folder, Plus, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function FolderPicker({ open, onClose, onSelect, currentFolderId }) {
  const [folders, setFolders] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/folders").then((r) => setFolders(r.data)).catch(() => {});
  }, [open]);

  const create = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/folders", { name: newName.trim() });
      setFolders([data, ...folders]);
      setNewName("");
      setCreating(false);
      toast.success("Folder created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-sm" data-testid="folder-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Move to folder</DialogTitle>
        </DialogHeader>

        <div className="space-y-2 max-h-[320px] overflow-y-auto">
          <button
            onClick={() => onSelect?.(null)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm border ${
              !currentFolderId ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 hover:bg-zinc-50"
            }`}
            data-testid="folder-pick-unfiled"
          >
            <X className="w-4 h-4" strokeWidth={1.75} /> Unfiled
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              onClick={() => onSelect?.(f.id)}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-sm text-sm border ${
                currentFolderId === f.id ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 hover:bg-zinc-50"
              }`}
              data-testid={`folder-pick-${f.id}`}
            >
              <span className="flex items-center gap-3">
                <Folder className="w-4 h-4" strokeWidth={1.75} /> {f.name}
              </span>
              <span className="font-mono text-[10px] opacity-60">{f.image_count}</span>
            </button>
          ))}
        </div>

        <DialogFooter className="flex sm:justify-between gap-2 pt-3 border-t border-zinc-100">
          {creating ? (
            <div className="flex gap-2 w-full">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Folder name"
                className="rounded-sm"
                data-testid="new-folder-name-input"
              />
              <Button onClick={create} className="rounded-sm bg-[#0055FF] hover:bg-[#0040CC]" data-testid="create-folder-confirm">
                Create
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setCreating(true)} className="rounded-sm" data-testid="new-folder-button">
              <Plus className="w-4 h-4 mr-2" /> New folder
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
